import {
  v2AdminClientsControllerListVersions,
  v2AdminClientsControllerReadVersion,
  v2AdminClientsControllerRestoreVersion,
  v2AdminNegotiationsControllerListVersions,
  v2AdminNegotiationsControllerReadVersion,
  v2AdminNegotiationsControllerRestoreVersion
} from '@idc-forms/api-client'
import type {
  V2EntityVersionDetail,
  V2EntityVersionSummary
} from '@idc-forms/schemas/api-v2/admin-entity-versions'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Historial de versiones de un expediente (ADR 0029, Fase 2).
 *
 * Las dos entidades comparten la forma de una versión, así que comparten el servicio: el
 * `kind` elige a qué recurso pegarle. Publicar dos servicios idénticos garantizaría que se
 * separen en cuanto uno de los dos cambie.
 *
 * El ALCANCE lo resuelve el servidor: pedir el historial de un expediente fuera de la cartera
 * responde 404, igual que pedir su detalle. Acá no hay nada que filtrar.
 */

export type VersionedEntityKind = 'client' | 'negotiation'

export const entityVersionsService = {
  /** La línea de tiempo, de la más nueva a la más vieja. Sin snapshots: es para pintar la lista. */
  timeline: async (
    accessToken: string,
    kind: VersionedEntityKind,
    id: string
  ): Promise<V2EntityVersionSummary[]> => {
    const request = {
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    }

    const result =
      kind === 'client'
        ? await v2AdminClientsControllerListVersions(request)
        : await v2AdminNegotiationsControllerListVersions(request)

    return result.data.data as V2EntityVersionSummary[]
  },

  /** Una versión con su contenido y lo que cambió respecto de la anterior. */
  detail: async (
    accessToken: string,
    kind: VersionedEntityKind,
    id: string,
    version: number
  ): Promise<V2EntityVersionDetail> => {
    const request = {
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, version }
    }

    const result =
      kind === 'client'
        ? await v2AdminClientsControllerReadVersion(request)
        : await v2AdminNegotiationsControllerReadVersion(request)

    return result.data as V2EntityVersionDetail
  },

  /**
   * Devuelve el expediente al contenido de una versión.
   *
   * NO rebobina el historial: crea una versión nueva con ese contenido, así que la
   * restauración queda registrada y se puede deshacer con otra.
   */
  restore: async (
    accessToken: string,
    kind: VersionedEntityKind,
    id: string,
    version: number
  ): Promise<void> => {
    const request = {
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, version }
    }

    if (kind === 'client') {
      await v2AdminClientsControllerRestoreVersion(request)
      return
    }
    await v2AdminNegotiationsControllerRestoreVersion(request)
  }
}
